import { NavigationError } from '@angular/router';

/**
 * Browser-reported messages for a failed dynamic `import()` of a
 * content-hashed lazy chunk (GF-4700). Wording differs by engine — there is
 * no shared error class (Angular's esbuild-based builder emits plain
 * `TypeError`s, not a `ChunkLoadError` the way webpack does) — so the check
 * matches on the small set of known browser message patterns instead.
 */
const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
] as const;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * `window.location.assign` isn't a spyable property in Chrome (non-writable,
 * non-configurable), so it's wrapped in an object method — tests spy on
 * `locationAssign.call` instead of touching `window.location`.
 */
export const locationAssign = {
  call: (url: string): void => window.location.assign(url),
};

/**
 * Recovers from the "stale tab" class of dead clicks (GF-4700): a tab left
 * open across a deploy still holds the previous build's `index.html`, so
 * navigating to a lazy route not yet loaded this session 404s on that
 * route's chunk. The router surfaces this as a swallowed `NavigationError`
 * with no visible feedback (zoneless change detection means nothing
 * re-renders either).
 *
 * On a chunk-load failure, force a full navigation to the attempted URL so
 * the tab picks up the new build's `index.html` and lands on the requested
 * page. Guarded to one retry per URL per tab (sessionStorage) so a
 * genuinely broken chunk — as opposed to a stale build — can't reload-loop.
 */
export function handleChunkLoadNavigationError(event: NavigationError): void {
  if (!isChunkLoadError(event.error)) return;

  const retryFlagKey = `gf-chunk-reload:${event.url}`;
  if (sessionStorage.getItem(retryFlagKey)) return;

  sessionStorage.setItem(retryFlagKey, '1');
  // `location.reload()` reloads the current address-bar URL, but a failed
  // client-side navigation never updates it — `assign` is what actually
  // lands the tab on the page the user clicked.
  locationAssign.call(event.url);
}
